// ─────────────────────────────────────────────────────────────────────────────
// V2Auth — sign-in and account screens in the v2 design (outside the app shell).
//   V2Login          /login                 POST /api/auth/login { email, password }
//   V2Register       /register              POST /api/auth/createuser { name, email, password }
//   V2Forgot         /forgot-password       POST /api/auth/forgot-password → /verify-otp → /reset-password
//   V2AcceptInvite   /accept-invite/:token  GET /api/auth/invite-info/:token, POST /api/auth/accept-invite/:token { name, password }
//   V2GoogleCallback /auth/callback         same redirects as v1 (token → localStorage), without logging the URL
//   V2AccessDenied   /403
// Requests, validation (passwords ≥ 5 chars) and redirects match v1. Google sign-in uses the same URL as v1.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./v2.css";
import "./v2-pages.css";
import API_BASE from "../apiConfig";
import usePageTitle from "../hooks/usePageTitle";
import userContext from "../context/user/userContext";
import { useAppearance } from "./appearance";
import { Glyph } from "./icons";

const GOOGLE_URL = "https://api.eazy-billing.com/api/auth/google"; // same as v1
const ART = (n) => `${process.env.PUBLIC_URL || ""}/illustrations/onboarding/ob-${n}.svg`;
const post = async (path, body) => {
  const r = await fetch(`${API_BASE}${path}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  return { ok: r.ok, d };
};
const firstError = (d, fallback) => d?.error || d?.errors?.[0]?.msg || fallback;

function AuthShell({ art, title, sub, children, foot, wide }) {
  useAppearance();
  return (
    <div className="v2 v2au">
      <div className="v2-backdrop" aria-hidden="true" />
      <img className="v2ob-blob" src={ART("bg-blob")} alt="" aria-hidden="true" />
      <header className="v2au-top">
        <Link to="/" className="v2ob-brand v2au-brand"><span className="v2ob-brand-mark">e</span>EazyBilling</Link>
      </header>
      <main className={`v2au-stage${wide ? " is-wide" : ""}`}>
        {art && <div className="v2au-art" aria-hidden="true"><img src={ART(art)} alt="" /></div>}
        <section className="v2au-card">
          <h1>{title}</h1>
          {sub && <p className="v2au-sub">{sub}</p>}
          {children}
        </section>
      </main>
      {foot && <footer className="v2au-foot">{foot}</footer>}
    </div>
  );
}

function Password({ value, onChange, label = "Password", autoComplete = "current-password", autoFocus, hint }) {
  const [show, setShow] = useState(false);
  return (
    <label className="v2-field">
      <span className="v2-field-label">{label}</span>
      <span className="v2-input-wrap">
        <input className="v2-input v2au-input" type={show ? "text" : "password"} value={value} onChange={onChange} autoComplete={autoComplete} autoFocus={autoFocus} required />
        <button type="button" className="v2ac-eye" onClick={() => setShow((s) => !s)} aria-label={show ? "Hide password" : "Show password"}>{show ? "Hide" : "Show"}</button>
      </span>
      {hint}
    </label>
  );
}
const strength = (p) => (!p ? 0 : p.length < 5 ? 1 : p.length < 9 ? 2 : /[^a-zA-Z]/.test(p) && p.length >= 10 ? 4 : 3);
const Meter = ({ value }) => { const s = strength(value); return value ? <span className={`v2ac-meter v2ac-meter--${s}`}><i /><i /><i /><i /><span>{["", "Too short", "Fair", "Good", "Strong"][s]}</span></span> : null; };

function GoogleButton({ label = "Continue with Google" }) {
  return (
    <a className="v2-btn v2au-google" href={GOOGLE_URL}>
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      {label}
    </a>
  );
}
const Divider = () => <div className="v2au-or"><span>or</span></div>;
const Err = ({ children }) => (children ? <div className="v2-banner v2-banner--bad" role="alert"><Glyph name="alert" size={16} /> {children}</div> : null);

// ══════════════════════════════════════════════════════════════════════════════
export function V2Login({ showAlert }) {
  usePageTitle("Sign in");
  const navigate = useNavigate();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(() => {
    const e = new URLSearchParams(window.location.search).get("error");
    return !e ? "" : e === "no_token" ? "Google sign-in didn't finish. Please try again." : "Google sign-in failed. Please try again or use your email.";
  });
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { d } = await post("/api/auth/login", { email: f.email, password: f.password });
      if (d.success) { localStorage.setItem("token", d.authtoken); showAlert?.("Logged in successfully", "success"); navigate("/dashboard"); }
      else setErr(firstError(d, "That email and password don't match."));
    } catch { setErr("Couldn't reach EazyBilling. Check your connection and try again."); } finally { setBusy(false); }
  };
  return (
    <AuthShell art="welcome" title="Welcome back" sub="Sign in to manage your invoices, clients and GST."
      foot={<>New to EazyBilling? <Link to="/register">Create an account</Link></>}>
      <GoogleButton />
      <Divider />
      <form className="v2-form" onSubmit={submit}>
        <label className="v2-field"><span className="v2-field-label">Email</span>
          <input className="v2-input v2au-input" type="email" autoComplete="email" autoFocus required value={f.email} onChange={(e) => setF((p) => ({ ...p, email: e.target.value }))} /></label>
        <Password value={f.password} onChange={(e) => setF((p) => ({ ...p, password: e.target.value }))} />
        <div className="v2au-row"><Link to="/forgot-password" className="v2k-link">Forgot password?</Link></div>
        <Err>{err}</Err>
        <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
      </form>
    </AuthShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export function V2Register({ showAlert }) {
  usePageTitle("Create account");
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", cpassword: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 5) { setErr("Use at least 5 characters for your password."); return; }
    if (f.password !== f.cpassword) { setErr("The passwords don't match."); return; }
    setBusy(true);
    try {
      const { d } = await post("/api/auth/createuser", { name: f.name, email: f.email, password: f.password });
      if (d.success) { localStorage.setItem("token", d.authtoken); showAlert?.("Account created! Let's set up your business.", "success"); navigate("/dashboard"); }
      else setErr(firstError(d, "Couldn't create your account."));
    } catch { setErr("Couldn't reach EazyBilling. Check your connection and try again."); } finally { setBusy(false); }
  };
  const mismatch = f.cpassword && f.password !== f.cpassword;
  return (
    <AuthShell art="business" title="Create your account" sub="GST invoices, payments and reports in minutes."
      foot={<>Already have an account? <Link to="/login">Sign in</Link></>}>
      <GoogleButton label="Sign up with Google" />
      <Divider />
      <form className="v2-form" onSubmit={submit}>
        <label className="v2-field"><span className="v2-field-label">Your name</span><input className="v2-input v2au-input" autoComplete="name" autoFocus required value={f.name} onChange={set("name")} /></label>
        <label className="v2-field"><span className="v2-field-label">Email</span><input className="v2-input v2au-input" type="email" autoComplete="email" required value={f.email} onChange={set("email")} /></label>
        <div className="v2-form-row v2au-pair">
          <Password label="Password" autoComplete="new-password" value={f.password} onChange={set("password")} hint={<Meter value={f.password} />} />
          <Password label="Confirm" autoComplete="new-password" value={f.cpassword} onChange={set("cpassword")} hint={mismatch ? <span className="v2-field-err">Doesn't match yet</span> : null} />
        </div>
        <Err>{err}</Err>
        <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy}>{busy ? "Creating account…" : "Create account"}</button>
        <p className="v2au-legal">By creating an account you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
      </form>
    </AuthShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export function V2Forgot() {
  usePageTitle("Reset password");
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 email · 2 code · 3 new password · 4 done
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [note, setNote] = useState("");
  const run = async (fn) => { setErr(""); setBusy(true); try { await fn(); } catch { setErr("Couldn't reach EazyBilling. Check your connection and try again."); } finally { setBusy(false); } };
  const send = (e) => { e?.preventDefault(); run(async () => {
    const { ok, d } = await post("/api/auth/forgot-password", { email: email.trim() });
    if (!ok) { setErr(firstError(d, "Something went wrong.")); return; }
    setNote(d.message || "If that email is registered, a 6-digit code is on its way."); setStep(2);
  }); };
  const verify = (e) => { e.preventDefault(); run(async () => {
    const { ok, d } = await post("/api/auth/verify-otp", { email: email.trim(), otp });
    if (!ok) { setErr(firstError(d, "That code didn't work.")); return; }
    setToken(d.resetToken); setStep(3);
  }); };
  const reset = (e) => { e.preventDefault();
    if (pw.a.length < 5) { setErr("Use at least 5 characters."); return; }
    if (pw.a !== pw.b) { setErr("The passwords don't match."); return; }
    run(async () => {
      const { ok, d } = await post("/api/auth/reset-password", { resetToken: token, newPassword: pw.a });
      if (!ok) { setErr(firstError(d, "Couldn't reset your password.")); return; }
      setStep(4);
    });
  };
  const titles = { 1: ["Forgot your password?", "Enter your account email and we'll send you a 6-digit code."], 2: ["Check your email", <>We sent a code to <b>{email}</b>. It expires in 10 minutes.</>], 3: ["Choose a new password", "Use at least 5 characters."], 4: ["Password updated", "You can now sign in with your new password."] };
  return (
    <AuthShell art={step === 4 ? "done" : "tax"} title={titles[step][0]} sub={titles[step][1]} foot={step < 4 ? <>Remembered it? <Link to="/login">Sign in</Link></> : null}>
      <ol className="v2au-steps" aria-label={`Step ${Math.min(step, 3)} of 3`}>{[1, 2, 3].map((n) => <li key={n} className={n < step ? "is-done" : n === step ? "is-now" : ""} />)}</ol>
      {step === 1 && <form className="v2-form" onSubmit={send}>
        <label className="v2-field"><span className="v2-field-label">Email</span><input className="v2-input v2au-input" type="email" autoComplete="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <Err>{err}</Err>
        <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy}>{busy ? "Sending…" : "Send code"}</button>
      </form>}
      {step === 2 && <form className="v2-form" onSubmit={verify}>
        {note && <div className="v2-banner v2-banner--info"><Glyph name="inbox" size={16} /> {note}</div>}
        <label className="v2-field"><span className="v2-field-label">6-digit code</span>
          <input className="v2-input v2au-input v2au-otp" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} pattern="\d{6}" required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} /></label>
        <Err>{err}</Err>
        <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy || otp.length !== 6}>{busy ? "Checking…" : "Verify code"}</button>
        <div className="v2au-row v2au-row--split"><button type="button" className="v2k-link" onClick={() => { setStep(1); setOtp(""); setErr(""); }}>Use a different email</button><button type="button" className="v2k-link" disabled={busy} onClick={send}>Resend code</button></div>
      </form>}
      {step === 3 && <form className="v2-form" onSubmit={reset}>
        <Password label="New password" autoComplete="new-password" autoFocus value={pw.a} onChange={(e) => setPw((p) => ({ ...p, a: e.target.value }))} hint={<Meter value={pw.a} />} />
        <Password label="Confirm new password" autoComplete="new-password" value={pw.b} onChange={(e) => setPw((p) => ({ ...p, b: e.target.value }))} />
        <Err>{err}</Err>
        <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy}>{busy ? "Saving…" : "Update password"}</button>
      </form>}
      {step === 4 && <button type="button" className="v2-btn v2-btn--primary v2au-submit" onClick={() => navigate("/login")} autoFocus>Sign in</button>}
    </AuthShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
const ROLE = { admin: "Admin", accountant: "Accountant", sales: "Sales", inventory: "Inventory", hr: "HR", viewer: "Viewer" };
export function V2AcceptInvite({ showAlert }) {
  usePageTitle("Join your team");
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [bad, setBad] = useState("");
  const [f, setF] = useState({ name: "", password: "", cpassword: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    fetch(`${API_BASE}/api/auth/invite-info/${token}`).then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.error || "This invite isn't valid."); setInfo(d); })
      .catch((e) => setBad(e.message || "This invite isn't valid."));
  }, [token]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.password.length < 5) { setErr("Use at least 5 characters for your password."); return; }
    if (f.password !== f.cpassword) { setErr("The passwords don't match."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/auth/accept-invite/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: f.name, password: f.password }) });
      const d = await r.json().catch(() => ({}));
      if (d.success) { localStorage.setItem("token", d.authtoken); showAlert?.("Welcome to the team!", "success"); navigate("/dashboard"); }
      else setErr(firstError(d, "Couldn't accept the invite."));
    } catch { setErr("Couldn't reach EazyBilling. Check your connection and try again."); } finally { setBusy(false); }
  };
  if (bad) return (
    <AuthShell art="contact" title="This invite has expired" sub={`${bad} Ask the person who invited you to send a new one.`}
      foot={<>Already have an account? <Link to="/login">Sign in</Link></>}>
      <button type="button" className="v2-btn v2-btn--primary v2au-submit" onClick={() => navigate("/")}>Go to EazyBilling</button>
    </AuthShell>);
  return (
    <AuthShell art="contact" title={info ? `Join ${info.companyName || "your team"}` : "Loading your invite…"} sub={info ? "Set up your login to start working with your team." : ""}>
      {!info ? <div className="v2-skel" style={{ height: 220, borderRadius: 18 }} /> : <>
        <div className="v2au-invite">
          <span className="v2au-invite-mark">{(info.companyName || "?").trim().slice(0, 1).toUpperCase()}</span>
          <div><b>{info.companyName}</b><span>{info.email}</span></div>
          {info.role && <span className="v2-status v2-status--info">{ROLE[info.role] || info.role}</span>}
        </div>
        <form className="v2-form" onSubmit={submit}>
          <label className="v2-field"><span className="v2-field-label">Your name</span><input className="v2-input v2au-input" autoComplete="name" autoFocus required value={f.name} onChange={set("name")} /></label>
          <Password label="Create a password" autoComplete="new-password" value={f.password} onChange={set("password")} hint={<Meter value={f.password} />} />
          <Password label="Confirm password" autoComplete="new-password" value={f.cpassword} onChange={set("cpassword")} />
          <Err>{err}</Err>
          <button type="submit" className="v2-btn v2-btn--primary v2au-submit" disabled={busy}>{busy ? "Joining…" : "Join team"}</button>
          <p className="v2au-legal">You'll only see the parts of EazyBilling your role allows.</p>
        </form>
      </>}
    </AuthShell>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export function V2GoogleCallback() {
  usePageTitle("Signing in…");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token"), onboarding = params.get("onboarding"), error = params.get("error");
    if (error) { window.location.href = "/login?error=" + encodeURIComponent(error); return; }
    if (!token) { window.location.href = "/login?error=no_token"; return; }
    localStorage.setItem("token", token);
    window.location.href = onboarding === "true" ? "/dashboard" : "/dashboard/home"; // full reload, as v1
  }, []);
  useAppearance();
  return (
    <div className="v2 v2-boot" role="status" aria-live="polite">
      <div className="v2-backdrop" aria-hidden="true" />
      <div className="v2-boot-card"><span className="v2-boot-mark" aria-hidden="true">e</span><span className="v2-boot-bar" aria-hidden="true"><i /></span><span className="v2au-boot-text">Signing you in…</span></div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
export function V2AccessDenied() {
  usePageTitle("No access");
  const navigate = useNavigate();
  const { user } = useContext(userContext);
  const role = user?.role ? (ROLE[user.role] || user.role[0].toUpperCase() + user.role.slice(1)) : null;
  return (
    <AuthShell art="tax" title="You don't have access to this" sub={role ? `Your role (${role}) doesn't include this section of EazyBilling.` : "Your role doesn't include this section of EazyBilling."}>
      <ul className="v2au-list">
        <li><Glyph name="user" size={17} /> Ask your account owner to change your role if you need it.</li>
        <li><Glyph name="grid" size={17} /> Everything your role allows is still in the sidebar.</li>
      </ul>
      <div className="v2au-actions">
        <button type="button" className="v2-btn" onClick={() => navigate(-1)}><Glyph name="chevronLeft" size={16} stroke={2} /> Go back</button>
        <button type="button" className="v2-btn v2-btn--primary" onClick={() => navigate("/dashboard/home")} autoFocus>Go to dashboard</button>
      </div>
    </AuthShell>
  );
}
